import type { ReactNode } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill, Tooltip } from '@odo/shared-ui';
import type { ConversationQuickReply } from '../../../services/conversationQuickReplies';
import { TOOLTIP_OVER_MODAL } from '../../shared/ModalShell';
import { quickReplyDocText } from '../chat/quickReplyDocText';
import { THREAD_PILL_TONE, threadLabel } from './threadOptions';

interface QuickReplyAdminRowProps {
    reply: ConversationQuickReply;
    onEdit: (reply: ConversationQuickReply) => void;
    onDelete: (reply: ConversationQuickReply) => void;
}

export function QuickReplyAdminRow({ reply, onEdit, onDelete }: QuickReplyAdminRowProps) {
    return (
        <li className="flex items-start gap-4 px-4 py-3">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {reply.title}
                    </span>
                    <span className="font-mono text-[11px] text-gray-400 flex-shrink-0">
                        {reply.code}
                    </span>
                    <SolidStatusPill
                        value={reply.chatType}
                        label={threadLabel(reply.chatType)}
                        toneClassName={THREAD_PILL_TONE[reply.chatType]}
                        compact
                        className="flex-shrink-0"
                    />
                </div>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 break-words">
                    {quickReplyDocText(reply.bodyDoc)}
                </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <RowIconButton label="Edit quick reply" onClick={() => onEdit(reply)}>
                    <PencilIcon className="w-4 h-4" />
                </RowIconButton>
                <RowIconButton label="Delete quick reply" danger onClick={() => onDelete(reply)}>
                    <TrashIcon className="w-4 h-4" />
                </RowIconButton>
            </div>
        </li>
    );
}

interface RowIconButtonProps {
    label: string;
    danger?: boolean;
    onClick: () => void;
    children: ReactNode;
}

function RowIconButton({ label, danger = false, onClick, children }: RowIconButtonProps) {
    return (
        <Tooltip content={label} className={TOOLTIP_OVER_MODAL}>
            <button
                type="button"
                aria-label={label}
                onClick={onClick}
                className={`w-8 h-8 rounded-lg inline-flex items-center justify-center text-gray-400 transition-colors hover:bg-gray-100 ${
                    danger ? 'hover:text-red-600' : 'hover:text-gray-800'
                }`}
            >
                {children}
            </button>
        </Tooltip>
    );
}
